/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
// import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { Port, getTenantsToCreatePort, getDataCentersIdsNames, getHardwareByDatacenterId, DataCenterItem, Hardware } from "../../request/request";
import { useAppSelector } from "@redux/hooks";

interface Tenant {
  id: number;
  Name: string;
  Domain: string;
  OrgID: string;
  chargebeeCustomerId: string | null;
  jiraOrganizationId: string | null;
  nameDisplay: string;
  hasMFA: boolean;
  tenantTypeId: number;
  canAccessAlerts: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PortCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (portData: Partial<Port>) => Promise<void>;
}

export const PortCreateModal: React.FC<PortCreateModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  
  const [formData, setFormData] = useState<Partial<Port>>({
    portName: "",
    portNumber: "",
    portType: "",
    portStatus: "available",
    isAvailable: false,
    decommissioned: false,
    hardwareResourceId: undefined,
    clientAssignation: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Tenants state
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [selectedTenantNameDisplay, setSelectedTenantNameDisplay] = useState<string>("");

  // DataCenter and Hardware state
  const [dataCenters, setDataCenters] = useState<DataCenterItem[]>([]);
  const [allHardware, setAllHardware] = useState<Hardware[]>([]);
  const [isLoadingDataCenters, setIsLoadingDataCenters] = useState(false);
  const [isLoadingHardware, setIsLoadingHardware] = useState(false);
  const [selectedDatacenterId, setSelectedDatacenterId] = useState<string | undefined>(undefined);
  const [selectedDatacenterLabel, setSelectedDatacenterLabel] = useState<string>("");
  const [selectedHardwareLabel, setSelectedHardwareLabel] = useState<string>("");
  const { tenantId } = useAppSelector((state) => state.selection);

  // Fetch tenants and datacenters when modal opens
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      (async () => {
        try {
          setIsLoadingTenants(true);
          const token = await getAccessTokenSilently();
          const resp = await getTenantsToCreatePort(token, tenantId);
          const tenantsData = (resp as any)?.data?.tenants ?? [];
          setTenants(tenantsData);
        } catch (error) {
          console.error("Error fetching tenants:", error);
          setTenants([]);
        } finally {
          setIsLoadingTenants(false);
        }
      })();

      (async () => {
        try {
          setIsLoadingDataCenters(true);
          const token = await getAccessTokenSilently();
          const dcResp = await getDataCentersIdsNames(token);
          const dcRaw = (dcResp as any)?.data?.data ?? (dcResp as any)?.data ?? [];
          const shapedDataCenters: DataCenterItem[] = dcRaw.map((dc: any) => ({
            datacenterId: dc.datacenterId,
            name: dc.name,
            displayName: dc.displayName ?? dc.name,
          }));
          setDataCenters(shapedDataCenters);
        } catch (error) {
          console.error("Error fetching datacenters:", error);
          setDataCenters([]);
        } finally {
          setIsLoadingDataCenters(false);
        }
      })();
    }
  }, [isOpen, isAuthenticated, getAccessTokenSilently]);

  // Fetch hardware when datacenter is selected
  useEffect(() => {
    if (!isOpen || !isAuthenticated || !selectedDatacenterId) {
      setAllHardware([]);
      return;
    }

    (async () => {
      try {
        setIsLoadingHardware(true);
        const token = await getAccessTokenSilently();
        const hwResp = await getHardwareByDatacenterId(token, selectedDatacenterId);
        const hwRaw = (hwResp as any)?.data?.data ?? (hwResp as any)?.data ?? [];
        setAllHardware(hwRaw);
      } catch (error) {
        console.error("Error fetching hardware:", error);
        setAllHardware([]);
      } finally {
        setIsLoadingHardware(false);
      }
    })();
  }, [selectedDatacenterId, isOpen, isAuthenticated, getAccessTokenSilently]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        portName: "",
        portNumber: "",
        portType: "",
        portStatus: "available",
        isAvailable: false,
        decommissioned: false,
        hardwareResourceId: undefined,
        clientAssignation: "",
      });
      setSelectedTenantNameDisplay("");
      setSelectedDatacenterId(undefined);
      setSelectedDatacenterLabel("");
      setSelectedHardwareLabel("");
      setAllHardware([]);
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof Port, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBooleanChange = (field: keyof Port, value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTenantSelect = (nameDisplay: string) => {
    setSelectedTenantNameDisplay(nameDisplay);
    setFormData((prev) => ({
      ...prev,
      clientAssignation: nameDisplay,
    }));
  };

  const handleDataCenterSelect = (selected: string) => {
    if (selected === "Select an option") {
      setSelectedDatacenterId(undefined);
      setSelectedDatacenterLabel("");
      setAllHardware([]);
      setSelectedHardwareLabel("");
      setFormData((prev) => ({
        ...prev,
        hardwareResourceId: undefined,
      }));
    } else {
      // Find datacenter ID from display name
      const datacenter = dataCenters.find(dc => (dc.displayName || dc.name) === selected);
      if (datacenter) {
        setSelectedDatacenterId(datacenter.datacenterId);
        setSelectedDatacenterLabel(selected);
        // Reset hardware selection when datacenter changes
        setSelectedHardwareLabel("");
        setFormData((prev) => ({
          ...prev,
          hardwareResourceId: undefined,
        }));
      }
    }
  };

  const handleHardwareSelect = (selected: string) => {
    if (selected === "Select hardware") {
      setSelectedHardwareLabel("");
      setFormData((prev) => ({
        ...prev,
        hardwareResourceId: undefined,
      }));
    } else {
      // Find hardware ID from hostname
      const hardware = allHardware.find(hw => hw.hostname === selected);
      if (hardware) {
        setSelectedHardwareLabel(selected);
        setFormData((prev) => ({
          ...prev,
          hardwareResourceId: hardware.id,
        }));
      }
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Prepare payload according to API format
      const payload: any = {
        hardwareResourceId: formData.hardwareResourceId ? Number(formData.hardwareResourceId) : 0,
        // hardwareResourceId: formData.hardwareResourceId ? Number(formData.hardwareResourceId) : undefined,
        portDescription: formData.portName || "",
        portNumber: formData.portNumber || "",
        portType: formData.portType || "",
        portStatus: formData.portStatus || "available",
        clientAssignation: formData.clientAssignation || "",
        isAvailable: formData.isAvailable ?? false,
        decommissioned: formData.decommissioned ?? false,
      };
      
      await onSave(payload);
      onClose();
    } catch (error) {
      console.error("Error saving port:", error);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Get tenant options for dropdown
  const tenantOptions = tenants.map(t => t.nameDisplay).sort();

  // Get datacenter options for dropdown
  const dataCenterOptions = dataCenters.map(dc => dc.displayName || dc.name).sort();

  // Get hardware options for dropdown
  const hardwareOptions = allHardware.map(hw => hw.hostname).filter(Boolean).sort();

  // Check if hardware dropdown should be enabled
  const isHardwareEnabled = selectedDatacenterId !== undefined;

  return (
    <GenericModal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Create Port"
      customWidth="max-w-4xl"
    >
      <div className="space-y-6">
        {/* Location Selection */}
        <GenericCard isCollapsible={false} className="p-5">
          <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
            Location Selection
          </h3>
          <div className="flex flex-col space-y-4">
            {/* First row: Data Center and Hardware in 2 columns */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-medium">Data Center</label>
                <GenericDropdown
                  items={dataCenterOptions}
                  value={selectedDatacenterLabel}
                  onSelect={handleDataCenterSelect}
                  defaultLabel={isLoadingDataCenters ? "Loading..." : "Select DC"}
                  disabled={isLoadingDataCenters}
                  maxWidthClass="w-full"
                  dropdownMaxHeightClass="max-h-48"
                  fixedPosition={true}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-medium">Hardware</label>
                <GenericDropdown
                  items={hardwareOptions}
                  value={isLoadingHardware ? "Loading..." : selectedHardwareLabel}
                  onSelect={handleHardwareSelect}
                  disabled={!isHardwareEnabled || isLoadingHardware}
                  defaultLabel="Select Hardware"
                  maxWidthClass="w-full"
                  dropdownMaxHeightClass="max-h-48"
                  fixedPosition={true}
                />
              </div>
            </div>
          </div>
        </GenericCard>

        {/* Port Information */}
        <GenericCard isCollapsible={false} className="p-5">
          <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
            Port Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <GenericInput
              label="Port Description"
              value={formData.portName || ""}
              onChange={(value) => handleInputChange("portName", value)}
            />
            <GenericInput
              label="Port Number"
              value={formData.portNumber || ""}
              onChange={(value) => handleInputChange("portNumber", value)}
            />
            <GenericInput
              label="Port Type"
              value={formData.portType || ""}
              onChange={(value) => handleInputChange("portType", value)}
            />
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                Port Status
              </label>
              <GenericDropdown
                items={["im-state-admin-down", "im-state-up", "decommissioned", "im-state-down", "available"]}
                value={formData.portStatus || "available"}
                defaultLabel="Select Port Status"
                onSelect={(value) => handleInputChange("portStatus", value)}
                minWidthClass="min-w-[200px]"
                maxWidthClass="max-w-[240px]"
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                Client Assignation
              </label>
              <GenericDropdown
                items={tenantOptions}
                value={selectedTenantNameDisplay}
                defaultLabel={isLoadingTenants ? "Loading..." : "Select Tenant"}
                onSelect={handleTenantSelect}
                disabled={isLoadingTenants}
                minWidthClass="min-w-[200px]"
                maxWidthClass="max-w-[240px]"
                dropdownMaxHeightClass="max-h-48"
                fixedPosition={true}
              />
            </div>
            {/* Boolean fields as switches */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                Available
              </label>
              <div className="flex items-center gap-2">
                <GenericSwitch
                  checked={formData.isAvailable ?? false}
                  onChange={(value) => handleBooleanChange("isAvailable", value)}
                />
                <span className="text-sm text-semantic-text-secondary">
                  {formData.isAvailable ? "Yes" : "No"}
                </span>
              </div>
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                Decommissioned
              </label>
              <div className="flex items-center gap-2">
                <GenericSwitch
                  checked={formData.decommissioned ?? false}
                  onChange={(value) => handleBooleanChange("decommissioned", value)}
                />
                <span className="text-sm text-semantic-text-secondary">
                  {formData.decommissioned ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>
        </GenericCard>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <GenericButton 
          label="Cancel" 
          variant="secondary" 
          onClick={onClose}
          disabled={isSaving}
        />
        <GenericButton 
          label={isSaving ? "Creating..." : "Create"} 
          variant="brand" 
          onClick={handleSave}
          disabled={isSaving}
        />
      </div>
    </GenericModal>
  );
};
